<template>
    <div class="file-viewer-pdf tw:flex tw:flex-col tw:h-full tw:min-h-0" data-test="file-viewer-pdf">
        <file-viewer-controls class="tw:flex-wrap tw:gap-y-1" data-test="file-viewer-pdf-controls">
            <div class="tw:flex tw:items-center tw:gap-1" data-test="file-viewer-pdf-nav">
                <button
                    type="button"
                    class="button is-small is-white"
                    data-test="file-viewer-pdf-prev"
                    aria-label="Previous page"
                    :disabled="pageNumber <= 1"
                    @click="prevPage"
                >
                    Prev
                </button>
                <span class="tw:text-sm tw:tabular-nums" data-test="file-viewer-pdf-page" aria-live="polite">
                    Page {{ pageNumber }} of {{ numPages || '?' }}
                </span>
                <button
                    type="button"
                    class="button is-small is-white"
                    data-test="file-viewer-pdf-next"
                    aria-label="Next page"
                    :disabled="!numPages || pageNumber >= numPages"
                    @click="nextPage"
                >
                    Next
                </button>
            </div>
            <div class="tw:flex tw:items-center tw:gap-1 tw:ml-auto" data-test="file-viewer-pdf-zoom-controls">
                <button
                    type="button"
                    class="button is-small is-white"
                    data-test="file-viewer-pdf-zoom-out"
                    aria-label="Zoom out"
                    :disabled="scale <= minScale"
                    @click="zoomOut"
                >
                    <i class="uil uil-minus" aria-hidden="true" />
                </button>
                <span class="tw:text-sm tw:tabular-nums" data-test="file-viewer-pdf-zoom" aria-live="polite">
                    {{ Math.round(scale * 100) }}%
                </span>
                <button
                    type="button"
                    class="button is-small is-white"
                    data-test="file-viewer-pdf-zoom-in"
                    aria-label="Zoom in"
                    :disabled="scale >= maxScale"
                    @click="zoomIn"
                >
                    <i class="uil uil-plus" aria-hidden="true" />
                </button>
                <button
                    type="button"
                    class="button is-small is-white"
                    data-test="file-viewer-pdf-fit-width"
                    aria-label="Fit to width"
                    @click="fitToWidth"
                >
                    <i class="uil uil-expand-arrows-alt" aria-hidden="true" />
                </button>
            </div>
        </file-viewer-controls>

        <div
            ref="container"
            class="tw:flex-1 tw:min-h-0 tw:overflow-auto tw:p-4 tw:bg-gray-50"
            data-test="file-viewer-pdf-viewport"
        >
            <canvas ref="canvas" class="tw:block tw:bg-white tw:shadow-sm" data-test="file-viewer-pdf-canvas" />
        </div>
    </div>
</template>

<script setup>
// eslint-disable-next-line import/extensions -- Vite ?url asset import for pdf.js worker
import pdfWorkerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';
import { onBeforeUnmount, ref, watch } from 'vue';
import FileViewerControls from '@/components/file-viewer/FileViewerControls.vue';

const props = defineProps({
    src: {
        type: String,
        required: true,
    },
});

const emit = defineEmits(['ready', 'error']);

const canvas = ref(null);
const container = ref(null);
let pdfDoc = null;
let loadingTask = null;
let renderTask = null;
let loadId = 0;
/** Bumps on every render/fit request so slower earlier pages cannot paint over newer ones. */
let renderId = 0;
const numPages = ref(0);
const pageNumber = ref(1);
const minScale = 0.5;
const maxScale = 3;
const scaleStep = 0.25;
const scale = ref(1.25);

/**
 * @param {number} num
 */
async function renderPage(num) {
    if (!pdfDoc) {
        return;
    }

    const id = ++renderId;
    renderTask?.cancel();
    renderTask = null;

    const page = await pdfDoc.getPage(num);
    if (id !== renderId) {
        return;
    }

    const viewport = page.getViewport({ scale: scale.value });
    const c = canvas.value;
    if (!c || id !== renderId) {
        return;
    }

    c.width = Math.floor(viewport.width);
    c.height = Math.floor(viewport.height);
    const ctx = c.getContext('2d');
    renderTask = page.render({ canvasContext: ctx, canvas: c, viewport });
    try {
        await renderTask.promise;
    } catch (e) {
        if (id !== renderId || e?.name === 'RenderingCancelledException') {
            return;
        }
        emit('error', e);
    }
}

/**
 * @param {string} url
 */
async function loadPdf(url) {
    const id = ++loadId;
    try {
        // eslint-disable-next-line import/extensions -- pdfjs package subpath
        const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
        if (id !== loadId) {
            return;
        }
        pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

        // Fetch on the main thread (same auth path as DOCX/XLSX) so session cookies
        // and relative /media URLs work; pdf.js v6 also requires { url|data }, not a bare string.
        const resp = await fetch(url, { credentials: 'same-origin' });
        if (id !== loadId) {
            return;
        }
        if (!resp.ok) {
            throw new Error(`Failed to fetch PDF (${resp.status})`);
        }

        const arrayBuf = await resp.arrayBuffer();
        if (id !== loadId) {
            return;
        }
        const data = new Uint8Array(arrayBuf);
        const task = pdfjs.getDocument({ data });
        loadingTask = task;
        const doc = await task.promise;
        if (id !== loadId) {
            // If this load is stale, destroy the doc we just loaded and ignore the result.
            try {
                doc.destroy();
            } catch (e) {
                // ignore
            }
            return;
        }
        pdfDoc = doc;
        numPages.value = pdfDoc.numPages;
        pageNumber.value = Math.min(pageNumber.value, pdfDoc.numPages);
        await renderPage(pageNumber.value);
        if (id !== loadId) {
            return;
        }
        emit('ready');
    } catch (err) {
        // Ignore errors from stale loads so an old request cannot overwrite the current state
        if (id !== loadId) {
            return;
        }
        emit('error', err);
    }
}

function teardown() {
    renderId += 1;
    renderTask?.cancel();
    renderTask = null;
    // Some pdfjs versions expose destroy(), others provide cancel() on the loadingTask.
    if (loadingTask) {
        if (typeof loadingTask.destroy === 'function') {
            loadingTask.destroy();
        } else if (typeof loadingTask.cancel === 'function') {
            // cancel() is a safe fallback when destroy() is unavailable.
            loadingTask.cancel();
        }
    }
    loadingTask = null;
    pdfDoc = null;
}

function nextPage() {
    if (pageNumber.value < numPages.value) {
        pageNumber.value += 1;
        renderPage(pageNumber.value);
    }
}

function prevPage() {
    if (pageNumber.value > 1) {
        pageNumber.value -= 1;
        renderPage(pageNumber.value);
    }
}

function zoomIn() {
    scale.value = Math.min(maxScale, Number((scale.value + scaleStep).toFixed(2)));
    renderPage(pageNumber.value);
}

function zoomOut() {
    scale.value = Math.max(minScale, Number((scale.value - scaleStep).toFixed(2)));
    renderPage(pageNumber.value);
}

async function fitToWidth() {
    if (!pdfDoc || !container.value) {
        return;
    }

    const id = ++renderId;
    renderTask?.cancel();
    renderTask = null;

    try {
        const page = await pdfDoc.getPage(pageNumber.value);
        if (id !== renderId) {
            return;
        }

        const unscaled = page.getViewport({ scale: 1 });
        const availableWidth = Math.max(container.value.clientWidth - 16, 1);
        scale.value = Math.max(minScale, Math.min(maxScale, Number((availableWidth / unscaled.width).toFixed(2))));
        await renderPage(pageNumber.value);
    } catch (e) {
        if (id !== renderId) {
            return;
        }
        emit('error', e);
    }
}

watch(
    () => props.src,
    value => {
        pageNumber.value = 1;
        numPages.value = 0;
        scale.value = 1.25;
        teardown();
        if (value) {
            loadPdf(value);
        }
    },
    { immediate: true }
);

onBeforeUnmount(() => {
    loadId += 1;
    teardown();
});
</script>
